import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useNavigation } from '@react-navigation/native'
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import { Asset } from 'expo-asset'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { throttle } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dimensions, Keyboard, Platform, Text, TouchableOpacity, View } from 'react-native'
import { ms, ScaledSheet } from 'react-native-size-matters'
import { useDispatch, useSelector } from 'react-redux'
import useSWRInfinite from 'swr/infinite'
import {
  SearchEngineImageSearchResource,
  SearchEngineImageSearchResourceCollection,
} from '~api/types'
import { FormInputButton } from '~components/FormInputButton'
import { ImageSearchItem } from '~components/ImageSearchItem'
import Loading from '~components/Loading'
import { getModalStyleDefinition, ModalHeader } from '~components/ModalScreen'
import RenderError from '~components/RenderError'
import { EVENT_INTERNET_IMAGE_SEARCH } from '~constants/Analytics'
import {
  IMAGE_MAX_DIMENSION_ONE_SECTION,
  IMAGE_MAX_DIMENSION_OVER_ONE_SECTION,
} from '~constants/Settings'
import useChatRouteParams from '~hooks/useChatRouteParams'
import useColumns from '~hooks/useColumns'
import { analytics, functions, logEvent } from '~providers/firebase'
import { ApplicationState } from '~redux'
import { setChatImage } from '~redux/chat/actions'
import { setChatImageLoading } from '~redux/chat/chatSlice'
import { useTheme } from '~theme/ThemeManager'
import urlInfo from '~utils/urlInfo'

type InternalProps = {
  target: string
}

export default function MediaSourceScreen(): React.JSX.Element {
  const { target } = useLocalSearchParams<InternalProps>()

  return <InternetSource target={target} />
}

function InternetSource({ target }: InternalProps): React.JSX.Element {
  const styles = useStyles()
  const [query, setQuery] = useState('')
  const [queryInternal, setQueryInternal] = useState('') // throttled
  const adjustedColumns = useColumns(1.5)
  const dispatch = useDispatch()
  const { chat } = useSelector((state: ApplicationState) => state.chat)
  const chatRouteParams = useChatRouteParams()
  const router = useRouter()

  const throttled = useRef(throttle(setQueryInternal, 1000))

  const getFetchUrl = useCallback(() => {
    const args = new URLSearchParams()
    args.append('query', queryInternal)

    return `/api/v1/images/internet/search?${args.toString()}`
  }, [queryInternal])

  const navigation = useNavigation()

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        // Clear search state on dismiss
        setQuery('')
        setQueryInternal('')
      }
    }, []),
  )

  const { data, size, setSize, isValidating, error } =
    useSWRInfinite<SearchEngineImageSearchResourceCollection>((pageIndex, previousPageData) => {
      if (queryInternal === undefined || queryInternal.length === 0) {
        return null
      }

      // First
      if (pageIndex === 0) {
        return getFetchUrl()
      }

      // End
      if (previousPageData && previousPageData.links.next !== null) {
        return previousPageData.links.next
      }

      return null
    })

  const items = (Array.isArray(data) ? data : [])
    .flatMap((o) => o.data)
    .filter((item, index, array) => {
      return array.findIndex((o) => o.id === item.id) === index
    })

  const more =
    size > 0 &&
    data !== undefined &&
    data[size - 1] !== undefined &&
    data[size - 1]?.links.next !== null

  const processInternetImage = async (
    index: number,
    resource: NonNullable<Pick<SearchEngineImageSearchResource, 'url' | 'width' | 'height'>>,
  ) => {
    const {
      layout: { rows, columns },
    } = chat
    let url = resource.url

    const isManipulated = new RegExp(
      functions.app.options.projectId + '.cloudfunctions.net$',
      'i',
    ).test(urlInfo(url).hostname)

    const maxDimension =
      columns === 1 && rows === 1
        ? IMAGE_MAX_DIMENSION_ONE_SECTION
        : IMAGE_MAX_DIMENSION_OVER_ONE_SECTION

    if (isManipulated) {
      // Append the width and height query args
      url += `&width=${maxDimension}&height=${maxDimension}`
    }

    try {
      dispatch(
        setChatImageLoading({ chat: { id: chat.id, path: chat.path }, index, loading: true }),
      )

      const download =
        Platform.OS === 'web'
          ? url
          : await Asset.fromURI(url)
              .downloadAsync()
              .then((res) => res.localUri)

      if (download) {
        // Persist it
        dispatch(
          setChatImage({
            chat: { id: chat.id, path: chat.path },
            index,
            uri: download,
          }),
        )
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const itemPressed = async (item: SearchEngineImageSearchResource) => {
    if (!chat) {
      return
    }

    await processInternetImage(Number.parseInt(target), item)

    router.dismissTo(chatRouteParams)
  }

  const renderItem = ({ index, item }: ListRenderItemInfo<SearchEngineImageSearchResource>) => (
    <ImageSearchItem
      style={{ flex: 1 / adjustedColumns }}
      item={item}
      onPress={() => itemPressed(item)}
    />
  )

  useEffect(() => {
    if (!chat.path) {
      router.dismissTo(chatRouteParams)
    }
  }, [chat.path, chatRouteParams, router])

  const search = () => {
    Keyboard.dismiss()

    throttled.current(query)

    // Only log analytics event if query changed
    if (query !== queryInternal) {
      logEvent(analytics, EVENT_INTERNET_IMAGE_SEARCH, {
        query,
      })
    }
  }

  const renderHeader = (
    <View style={styles.content}>
      <FormInputButton
        input={{
          value: query,
          placeholder: 'Search term',
          onChangeText: setQuery,
          style: styles.formInput,
          onSubmitEditing: search,
          returnKeyLabel: 'Search',
          returnKeyType: 'search',
          maxLength: 80,
        }}
        button={{
          title: 'Search',
          onPress: search,
        }}
      />
    </View>
  )

  const renderEmptyList =
    queryInternal.length === 0 ? null : isValidating ? (
      <View style={styles.emptyContainer}>
        <Loading />
      </View>
    ) : error ? (
      <View style={styles.emptyContainer}>
        <RenderError title="Network Error" description={error.message} />
      </View>
    ) : (
      <View style={styles.emptyContainer}>
        <RenderError title="Oops, nothing here!" description={`No images have been found.`} />
      </View>
    )

  return (
    <>
      <View style={styles.inner}>
        <ModalHeader>
          <View style={styles.content}>
            <View style={styles.titleContainer}>
              <View style={styles.header}>
                <Text style={styles.title}>Search the internet</Text>
                <TouchableOpacity onPress={navigation.goBack}>
                  <FontAwesomeIcon icon={['fal', 'times-circle']} size={ms(24)} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ModalHeader>

        <FlashList<SearchEngineImageSearchResource>
          key={`internet-image-search-${adjustedColumns}`}
          contentContainerStyle={styles.listContainer}
          ListHeaderComponent={renderHeader}
          data={items}
          numColumns={adjustedColumns}
          masonry
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
          onEndReached={() => more && setSize(size + 1)}
          onEndReachedThreshold={0.25}
          ListEmptyComponent={renderEmptyList}
        />
      </View>
    </>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    container: {
      backgroundColor: theme[100],
      flexGrow: 0,
      width: Math.min(Dimensions.get('window').width * 0.8, ms(256)),
    },
    emptyContainer: {
      paddingHorizontal: '10@ms',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    listContainer: {
      backgroundColor: theme[100],
      paddingHorizontal: '10@mvs0.8',
      paddingVertical: '10@mvs0.8',
    },
    content: {
      flexGrow: 1,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    titleContainer: {
      marginBottom: '10@mvs0.8',
    },
    title: {
      fontSize: '18@ms',
      fontWeight: '700',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '12@ms0.4',
      fontWeight: '300',
      textAlign: 'center',
    },
    formInput: {
      backgroundColor: theme.white,
    },
    tagContainer: {
      marginEnd: '4@ms',
      marginVertical: '2@ms',
    },
    tag: {
      backgroundColor: theme[400],
      paddingVertical: '6@mvs',
      paddingHorizontal: '4@ms',
    },
    tagActive: {
      backgroundColor: theme.primary,
    },
    tagTitle: {
      fontSize: '10@ms0.2',
      color: theme.white,
    },
    tagContainerStyle: {
      marginBottom: '16@mvs',
    },
    itemSeparator: {
      height: '5@mvs0.8',
    },
  })
}
